import { useState, useEffect } from 'react';
import { useTheme } from '../../zustand/ThemeStore';
import type { UserType, EditData } from '../../types/settings';

interface EditUserModalProps {
  isOpen: boolean;
  user: UserType | null;
  loading: boolean;
  onSubmit: (data: EditData) => void;
  onCancel: () => void;
}

export default function EditUserModal({
  isOpen,
  user,
  loading,
  onSubmit,
  onCancel
}: EditUserModalProps) {
  const darkMode = useTheme((state) => state.theme);
  const [editData, setEditData] = useState<EditData>({});

  useEffect(() => {
    if (user) {
      setEditData({ 
        name: user.name, 
        designation: user.designation 
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    onSubmit(editData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 relative transition-colors duration-300 ${
        darkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-white border border-gray-200'
      }`}>
        <h2 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-[#053333]'}`}>
          Edit User Details
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
              Name
            </label>
            <input
              type="text"
              name="name"
              value={editData.name || ''}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent text-sm transition-all ${
                darkMode ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500' : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              }`}
            />
          </div>
          
          <div>
            <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
              Designation
            </label>
            <input
              type="text"
              name="designation"
              value={editData.designation || ''}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent text-sm transition-all ${
                darkMode ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500' : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              }`}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              loading 
                ? 'opacity-50 cursor-not-allowed'
                : darkMode 
                  ? 'bg-[#1a2424] text-gray-300 hover:bg-[#053333]' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium bg-[#1a9f6b] text-white hover:bg-[#148856] flex items-center justify-center ${
              loading ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
